import { OddsFormat } from '@/hooks/use-auth-user-settings';
import { ParseKeys, TFunction } from 'i18next';

import { SbkPromo, SbkPromoCriterion } from '../../types';
import { OddsComputationState } from '../betslip-utils';
import { minMultiplierCriterion } from './min-multiplier-criterion';
import { minNumberOfPicksCriterion } from './min-number-of-picks-criterion';

export type CriteriaState = OddsComputationState;

export type CriterionValidator = {
    validate: (criterion: SbkPromoCriterion, state: CriteriaState, betId: string) => boolean;
    /** whether the bet could ever satisfy the criterion; false hides the promo from the bet's widget */
    isVisibleForBet: (criterion: SbkPromoCriterion, betId: string) => boolean;
    translationKey: ParseKeys<'sbk_promo_errors'>;
    getTranslationParams: (criterion: SbkPromoCriterion, oddsFormat: OddsFormat) => Record<string, unknown>;
};

export const criterionValidators: Record<string, CriterionValidator> = {
    MIN_NUMBER_OF_PICKS: minNumberOfPicksCriterion,
    MIN_MULTIPLIER: minMultiplierCriterion,
    // Add additional validators here
};

/**
 * Criterion types without a validator are ignored throughout (server-side validation only).
 */
export const isPromoVisibleForBet = (promo: SbkPromo, betId: string) =>
    (promo.criteria ?? []).every(criterion => {
        const validator = criterionValidators[criterion.type];
        return validator ? validator.isVisibleForBet(criterion, betId) : true;
    });

export const getUnsatisfiedCriterion = (promo: SbkPromo, state: CriteriaState, betId: string) =>
    (promo.criteria ?? []).find(criterion => {
        const validator = criterionValidators[criterion.type];
        return validator ? !validator.validate(criterion, state, betId) : false;
    });

export const arePromoCriteriaSatisfied = (promo: SbkPromo, state: CriteriaState, betId: string) =>
    !getUnsatisfiedCriterion(promo, state, betId);

/**
 * Maps campaign_token to the translated error of the promo's first unsatisfied
 * criterion; satisfied promos are omitted.
 */
export const getPromoCriteriaErrorMessages = (
    promos: SbkPromo[],
    state: CriteriaState,
    betId: string,
    t: TFunction<'sbk_promo_errors'>,
    oddsFormat: OddsFormat
) =>
    promos.reduce<Record<string, string>>((acc, promo) => {
        const criterion = getUnsatisfiedCriterion(promo, state, betId);
        const validator = criterion ? criterionValidators[criterion.type] : undefined;
        if (criterion && validator) {
            acc[promo.campaign_token] = t(
                validator.translationKey,
                validator.getTranslationParams(criterion, oddsFormat)
            );
        }
        return acc;
    }, {});
